import { addDays, differenceInCalendarDays, format, isSameDay, parseISO } from "date-fns";
import type { Process } from "../../api/types";
import { parseUtcDate } from "../../utils";
import { quantile } from "./processDurationStats";
import type { ProcessInstance } from "./processGraphModel";
import { normalizeSearch, runApps } from "./processSearch";

export type ComparisonMode = "duration" | "people" | "period" | "runs" | "apps" | "processes";
export type DurationChoice = "longerShorter" | "longerTypical" | "shorterTypical";
export type ComparisonGroup = { label: string; process: Process; runs: ProcessInstance[] };
export type ProcessComparison = {
  mode: ComparisonMode;
  a: ComparisonGroup;
  b: ComparisonGroup;
  excluded?: number;
  duration?: DurationChoice;
  period?: { a: [Date, Date]; b: [Date, Date] };
  useAll?: boolean;
};
// Normalize step labels when matching nodes from different processes. The graph also checks app.
export const comparisonStepKey = (name: string) => normalizeSearch(name);
export function durationGroups(
  runs: ProcessInstance[],
  choice: DurationChoice,
): {
  a: ProcessInstance[];
  b: ProcessInstance[];
  ranges: { a: [number | null, number | null]; b: [number | null, number | null] };
} | null {
  if (runs.length < (choice === "longerShorter" ? 2 : 3)) return null;
  const ordered = [...runs].sort(
    (a, b) => a.source.avgDuration - b.source.avgDuration || a.id.localeCompare(b.id),
  );
  const durations = ordered.map(run => run.source.avgDuration);
  // The displayed, minute-rounded cutoffs also define membership. This keeps equal durations
  // together and lets rounding shift a few cases rather than showing misleading boundaries.
  // With only two cases, use one midpoint cutoff for the shorter/longer comparison.
  const lower = Math.round(quantile(durations, runs.length === 2 ? 0.5 : 1 / 3) / 60) * 60;
  const upper = Math.round(quantile(durations, runs.length === 2 ? 0.5 : 2 / 3) / 60) * 60;
  const shorter = ordered.filter(run => run.source.avgDuration < lower);
  const longer = ordered.filter(run => run.source.avgDuration > upper);
  const typical = ordered.filter(
    run => run.source.avgDuration >= lower && run.source.avgDuration <= upper,
  );
  const a = choice === "shorterTypical" ? shorter : longer;
  const b = choice === "longerShorter" ? shorter : typical;
  if (!a.length || !b.length) return null;
  return {
    a,
    b,
    ranges: {
      a: choice === "shorterTypical" ? [null, lower] : [upper, null],
      b: choice === "longerShorter" ? [null, lower] : [lower, upper],
    },
  };
}

/** Adjacent calendar periods, ending at the start of tomorrow in the viewer's timezone. */
export function comparisonPeriods(now = new Date()): { a: [Date, Date]; b: [Date, Date] } {
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const middle = new Date(end);
  middle.setDate(middle.getDate() - 30);
  const start = new Date(middle);
  start.setDate(start.getDate() - 30);
  return { a: [middle, end], b: [start, middle] };
}
export const runsInPeriod = (runs: ProcessInstance[], [from, to]: [Date, Date]) =>
  runs.filter(run => {
    const time = parseUtcDate(run.source.traceStart).getTime();
    return time >= from.getTime() && time < to.getTime();
  });

// Date pickers include the last calendar day; run filtering uses an exclusive upper bound.
export const periodFromDates = (from: string, to: string): [Date, Date] => [
  parseISO(from),
  addDays(parseISO(to), 1),
];
export const periodDates = ([from, to]: [Date, Date]) => ({
  from: format(from, "yyyy-MM-dd"),
  to: format(addDays(to, -1), "yyyy-MM-dd"),
});

/** Offer the adjacent window when the other side covers the last N calendar days. */
export function previousPeriodFor([from, to]: [Date, Date], now = new Date()) {
  const days = differenceInCalendarDays(to, from);
  if (days < 1 || !isSameDay(to, addDays(now, 1))) return undefined;
  return { ...periodDates([addDays(from, -days), from]), days };
}
export function appGroups(runs: ProcessInstance[], a: string, b: string) {
  const left: ProcessInstance[] = [];
  const right: ProcessInstance[] = [];
  let excluded = 0;
  for (const run of runs) {
    const apps = runApps(run);
    const inA = apps.includes(a);
    const inB = apps.includes(b);
    if (inA && inB) excluded++;
    else if (inA) left.push(run);
    else if (inB) right.push(run);
  }
  return { a: left, b: right, excluded };
}
